import { MarkdownText } from '../markdown-text';
import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from '../dialogs/dialog';
import { ListCourseDto } from '../../general/dtos';
import { useAuth } from '../../providers/auth-provider';
import { useProducts } from '../../providers/products-provider';
import { formatCoursePrice } from '../../general/currency';
import { CatalogueOverview } from '../catalogue-overview';
import { coursesOverview } from '../../content/catalogue-overviews';
import { CoursePurchase, pendingCourseStorage } from './course-purchase';

export function ProductDescription({ onBackToList }: { onBackToList?: () => void }) {
    const { courses, selectedCourse, setSelectedCourse, loading, refresh } = useProducts();
    const auth = useAuth();
    const authenticated = auth.getIsAuthenticated();
    const [showSignIn, setShowSignIn] = useState(false);
    const [signInError, setSignInError] = useState('');
    const [buyingCourse, setBuyingCourse] = useState<ListCourseDto | null>(null);

    useEffect(() => {
        if (!authenticated || loading) return;
        const savedKey = sessionStorage.getItem(pendingCourseStorage);
        if (!savedKey) return;
        const course = courses.find(item => item.course_key === savedKey);
        sessionStorage.removeItem(pendingCourseStorage);
        if (course && course.price != null && course.price > 0) {
            setSelectedCourse(course);
            setBuyingCourse(course);
        }
    }, [authenticated, loading, courses, setSelectedCourse]);

    const authenticate = async (signup: boolean) => {
        setSignInError('');
        try {
            const returnTo = window.location.pathname + window.location.search + window.location.hash;
            if (signup) await auth.signup(returnTo);
            else await auth.login(returnTo);
        } catch {
            setSignInError('Could not open sign in. Please try again.');
        }
    };

    const buySelectedCourse = () => {
        if (!selectedCourse || selectedCourse.price == null || selectedCourse.price <= 0) return;
        if (!authenticated) {
            sessionStorage.setItem(pendingCourseStorage, selectedCourse.course_key);
            setSignInError('');
            setShowSignIn(true);
        } else {
            setBuyingCourse(selectedCourse);
        }
    };

    if (!selectedCourse) {
        return <div className="course-description-pane">
            <div className="course-description-scroll"><CatalogueOverview content={coursesOverview} /></div>
            <div className="course-description-actions course-description-actions-mobile-only p-3 border-top-1 surface-border">
                <Button label="Back to the list" icon="pi pi-arrow-left" className="mobile-details-back p-button-outlined"
                    onClick={onBackToList} />
            </div>
        </div>;
    }
    return (
        <div className="course-description-pane">
            <div className="course-description-scroll catalogue-content p-4">
                <h2 className="mt-0">{selectedCourse.name}</h2>
                <p className="catalogue-price">{formatCoursePrice(selectedCourse.price)}</p>
                {selectedCourse.price != null && <small className="catalogue-note">Includes applicable taxes.</small>}
                <MarkdownText text={selectedCourse.description || 'No description available.'} />
            </div>
            <div className="course-description-actions p-3 border-top-1 surface-border">
                <Button label="Back to the list" icon="pi pi-arrow-left" className="mobile-details-back p-button-outlined"
                    onClick={onBackToList} />
                <Button label="Buy" icon="pi pi-shopping-cart" aria-label={`Buy ${selectedCourse.name}`}
                    disabled={auth.getIsLoading() || selectedCourse.price == null || selectedCourse.price <= 0}
                    onClick={buySelectedCourse} />
            </div>
            <Dialog header="Sign in to buy a product" visible={showSignIn} modal className="theme-content"
                style={{ width: '28rem', maxWidth: '95vw' }} onHide={() => { setShowSignIn(false); sessionStorage.removeItem(pendingCourseStorage); }}>
                <p>Sign in or create an account to continue with your purchase.</p>
                {signInError && <p role="alert">{signInError}</p>}
                <div className="flex gap-2">
                    <Button label="Sign in" onClick={() => authenticate(false)} />
                    <Button label="Sign up" outlined onClick={() => authenticate(true)} />
                </div>
            </Dialog>
            <CoursePurchase course={buyingCourse} onHide={() => setBuyingCourse(null)} onPurchased={refresh} />
        </div>
    );
}
